import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Clock, PanelTop, ReceiptText, Settings } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router";

import { HelpHint } from "@/components/help";
import { HELP_KEYS } from "@/lib/help-context";
import { useOrganization } from "@/auth/organization";
import {
  Button,
  ErrorAlert,
  ErrorState,
  FullPageSpinner,
  Modal,
  cx,
  Badge,
  KeyValue,
  buttonClass,
  Breadcrumb,
  Section,
  LinkButton,
} from "@/components/ui";
import {
  domainInitials,
  domainKeys,
  domainsApi,
  longDate,
  useDomainDetail,
  type Domain,
  type Order,
  type OrderItem,
  type PillTone,
} from "@/lib/domains";
import { civilDay, formatMoney } from "@/lib/format";
import type { SupportContext } from "@/lib/supportContext";

import { clockTime, useNow } from "./purchase/helpers";
import { GetHelpLink } from "@/components/support/GetHelpLink";
import {
  DomainIcon,
  StepPill,
  Heading,
  HeadTile,
  DomainPage,
  TwoColumns,
  ProgressSection,
  Notice,
  ConnectRow,
  type Step,
} from "@/components/domains/parts";

const POLL_MS = 3000;
/** After this long without an answer the registration screen switches to "We're confirming…". */
const SLOW_MS = 2 * 60_000;

const inProgress = (i: OrderItem) => i.status === "queued" || i.status === "submitted";
const isFailed = (i: OrderItem) =>
  i.status === "failed" || i.status === "refunded" || i.status === "cancelled";
/** Order number is this order's own support reference — already shown to the customer as one. */
const orderSupportContext = (order: Order, item: OrderItem, reason: string): SupportContext => ({
  resourceType: "domain",
  resourceId: item.fqdn,
  reference: order.number,
  reason,
});

/** Find a domain with the transfer form open and this name filled in: a resubmission is a new order. */
const resubmitTransferUrl = (fqdn: string) => `/domains/find?transfer=1&fqdn=${encodeURIComponent(fqdn)}`;

const PAST: Record<OrderItem["kind"], string> = {
  register: "registered",
  transfer: "transferred",
  renew: "renewed",
  restore: "restored",
};

/** `/domains/orders/:orderId` — what happened to each domain of an order, polled until the registries answer. */
export function DomainOrderPage() {
  const { orderId = "" } = useParams();
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const order = useQuery({
    queryKey: domainKeys.order(orgId, orderId),
    queryFn: () => domainsApi.order(orgId, orderId),
    refetchInterval: (q) => (q.state.data?.items.some(inProgress) ? POLL_MS : false),
  });

  // When the last registry answers, refresh the domain list and details that were cached as pending.
  const active = order.data?.items.some(inProgress) ?? false;
  const wasActive = useRef(active);
  useEffect(() => {
    if (wasActive.current && !active) void qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
    wasActive.current = active;
  }, [active, orgId, qc]);

  if (order.isPending) return <FullPageSpinner />;
  if (order.isError) return <ErrorState error={order.error} onRetry={() => void order.refetch()} />;
  const o = order.data;
  if (o.payment_status !== "paid") return <PaymentPending order={o} />;
  if (o.items.length === 1) return <SingleItem order={o} item={o.items[0]!} />;
  return <MultiItem order={o} />;
}

// ── shared bits ─────────────────────────────────────────────────────────────
function Crumbs({ order, label }: { order: Order; label: string }) {
  const { orgId } = useOrganization();
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <Breadcrumb
        items={[
          { label: "Domains", to: "/domains" },
          { label: "Orders", to: "/domains/orders" },
          {
            label: order.items[0]?.fqdn ?? order.number,
            to: order.items[0]?.domain_id ? `/domains/${order.items[0].domain_id}` : undefined,
          },
          { label },
        ]}
      />
      <CancelOrderAction orgId={orgId} order={order} />
    </div>
  );
}

const ITEM_KIND: Record<Order["items"][number]["kind"], string> = {
  register: "Registration",
  transfer: "Transfer",
  renew: "Renewal",
  restore: "Restore",
};

function CancelOrderAction({ orgId, order }: { orgId: string; order: Order }) {
  const qc = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => domainsApi.cancelOrder(orgId, order.id),
    onSuccess: (updated) => {
      qc.setQueryData(domainKeys.order(orgId, order.id), updated);
      void qc.invalidateQueries({ queryKey: ["domains", orgId, "orders"] });
    },
  });
  const [confirming, setConfirming] = useState(false);
  if (order.payment_status !== "unpaid" || order.status !== "pending_payment") return null;
  return (
    <div className="flex items-center gap-2">
      <Button
        size="lg"
        variant="secondary"
        requires="domains:manage"
        loading={cancel.isPending}
        onClick={() => {
          cancel.reset();
          setConfirming(true);
        }}
      >
        {cancel.isPending ? "Cancelling…" : "Cancel order"}
      </Button>
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={`Cancel order ${order.number}?`}
        footer={
          <>
            <Button size="lg" variant="secondary" onClick={() => setConfirming(false)}>
              Keep order
            </Button>
            <Button size="lg" variant="danger" loading={cancel.isPending} onClick={() => cancel.mutate()}>
              Cancel order
            </Button>
          </>
        }
      >
        <ErrorAlert error={cancel.error} />
        <p className="text-sm text-fg-muted">
          Nothing has been charged. The {order.items.length === 1 ? "name" : "names"} held for this order{" "}
          {order.items.length === 1 ? "is" : "are"} released:
        </p>
        <ul className="divide-y divide-border rounded-lg border border-border text-sm">
          {order.items.map((item) => (
            <li key={item.id} className="flex justify-between gap-3 px-3 py-2">
              <span className="truncate font-medium text-fg">{item.fqdn}</span>
              <span className="shrink-0 text-fg-muted">
                {ITEM_KIND[item.kind]} · {item.years} {item.years === 1 ? "year" : "years"}
              </span>
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  );
}

function PaymentPending({ order }: { order: Order }) {
  const cancelled = order.status === "cancelled";
  // The processor refused it (or staff closed it as never taken): it isn't still being confirmed.
  const failed = !cancelled && order.payment_status === "failed";
  return (
    <DomainPage>
      <Crumbs
        order={order}
        label={cancelled ? "cancelled" : failed ? "payment failed" : "awaiting payment"}
      />
      <Heading
        size={24}
        tile={
          <HeadTile tone={cancelled ? "success" : failed ? "danger" : "pending"}>
            <ReceiptText className="size-5" />
          </HeadTile>
        }
        title={
          cancelled
            ? `Order ${order.number} cancelled`
            : failed
              ? `Order ${order.number} payment failed`
              : `Order ${order.number} awaiting payment`
        }
        status={
          <Badge size="md" tone={cancelled ? "neutral" : failed ? "danger" : "warning"}>
            {cancelled ? "Cancelled" : failed ? "Payment failed" : "Awaiting payment"}
          </Badge>
        }
        subtitle={
          cancelled
            ? "No payment was taken and the held domain names were released."
            : failed
              ? "The payment didn't go through, so nothing was charged and no domain was registered."
              : "Your order is saved securely while payment is being confirmed."
        }
      />
      {!cancelled && !failed && (
        <Notice tone="processing" title="Payment confirmation is still in progress">
          Do not start another checkout with a different card yet. This order will update when the payment
          processor responds.
        </Notice>
      )}
      <Receipt order={order} />
    </DomainPage>
  );
}

/** "just now" / "today at 10:42 AM" / "on 3 Oct 2026" */
function when(iso: string | null, now: number): string {
  if (!iso) return "";
  const t = new Date(iso);
  if (now - t.getTime() < 2 * 60_000) return "just now";
  if (civilDay(t) === civilDay(now)) return `today at ${clockTime(iso)}`;
  return `on ${longDate(iso)}`;
}

function years(n: number): string {
  return `${n} ${n === 1 ? "year" : "years"}`;
}

/** A step's label with the date it was reached (or a note) underneath. */
function dated(text: string, iso: string | null | undefined, note?: string): ReactNode {
  const sub = iso ? longDate(iso) : note;
  return (
    <>
      {text}
      {sub && <span className="block text-xs font-normal text-fg-subtle">{sub}</span>}
    </>
  );
}

/**
 * A transfer in's timeline (PHP-3941): Submitted → Auth code accepted → Waiting for current registrar (up to 5
 * days) → Completed, or an end as Rejected or Cancelled, each with the date it was reached.
 */
export function transferSteps(item: OrderItem): Step[] {
  const failed = isFailed(item);
  const done = item.status === "completed";
  const accepted = Boolean(item.submitted_at);
  const cancelled = item.failure_kind === "cancelled" || item.status === "cancelled";
  const waiting: Step["state"] = done
    ? "completed"
    : failed
      ? accepted
        ? "completed"
        : "not_yet"
      : accepted
        ? "in_progress"
        : "not_yet";
  return [
    { label: dated("Submitted", item.created_at), state: "completed" },
    {
      label: dated("Auth code accepted", item.submitted_at),
      state: accepted ? "completed" : failed ? "failed" : "in_progress",
    },
    {
      label: dated(
        "Waiting for current registrar (up to 5 days)",
        item.waiting_since,
        waiting === "in_progress" && item.completes_by
          ? `Completes by ${longDate(item.completes_by)} at the latest`
          : undefined,
      ),
      state: waiting,
    },
    failed
      ? { label: dated(cancelled ? "Cancelled" : "Rejected", item.completed_at), state: "failed" }
      : { label: dated("Completed", item.completed_at), state: done ? "completed" : "not_yet" },
  ];
}

/** The steps of one item, worded per kind (Figma "Registration / Transfer / Recovery progress"). */
function progressSteps(item: OrderItem, paid: string): Step[] {
  if (item.kind === "transfer") return transferSteps(item);
  const sent: Step["state"] = item.status === "queued" ? "in_progress" : "completed";
  const done = item.status === "completed";
  const failed = isFailed(item);
  const waiting: Step["state"] = done
    ? "completed"
    : failed
      ? "failed"
      : item.status === "submitted"
        ? "in_progress"
        : "not_yet";
  const last: Step["state"] = done ? "completed" : "not_yet";
  const labels = {
    register: [
      `Payment authorized — ${paid}`,
      "Registration request sent to registry",
      "Registry confirmation",
      "Domain appears in My Domains",
    ],
    restore: [
      `Redemption fee paid — ${paid}`,
      "Recovery request sent to registry",
      "Registry restoring domain",
      "DNS and services reconnected",
    ],
    renew: [
      `Payment taken — ${paid}`,
      "Renewal sent to registry",
      "Registry confirmation",
      "New expiry date recorded",
    ],
  }[item.kind];
  return [
    { label: labels[0], state: "completed" },
    { label: labels[1], state: failed ? "completed" : sent },
    { label: labels[2], state: waiting },
    { label: labels[3], state: last },
  ];
}

function NextStepEmail() {
  return (
    <ConnectRow
      icon={<DomainIcon name="mail" size={20} />}
      tile="warning"
      title="Set up professional email"
      detail="Mailboxes on your own domain are coming soon"
      status={
        <Badge size="md" tone="neutral">
          Coming soon
        </Badge>
      }
    />
  );
}

function ConnectNext({ domain, domainId }: { domain: Domain | undefined; domainId: string }) {
  if (domain?.site) {
    return (
      <ConnectRow
        first
        icon={<DomainIcon name="link" size={20} />}
        tile="success"
        title="Connected to your website"
        detail={`Points to ${domain.site.name}`}
        action={
          <Link to={`/domains/${domainId}/connection`} className={buttonClass("secondary", "lg")}>
            View connection
          </Link>
        }
      />
    );
  }
  return (
    <ConnectRow
      first
      icon={<DomainIcon name="link" size={20} />}
      tile="success"
      title="Connect to your website"
      detail={`Make ${domain?.display_name ?? "this domain"} your website's custom domain`}
      action={
        <LinkButton
          to={`/domains/${domainId}/connect`}
          className={buttonClass("brand", "lg")}
          requires="domains:manage"
        >
          Connect
        </LinkButton>
      }
    />
  );
}

function Rows({ children }: { children: ReactNode }) {
  return <div className="flex flex-col [&>*+*]:border-t [&>*+*]:border-border">{children}</div>;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 py-3 text-[13px]">
      <span className="text-[#5c5551]">{label}</span>
      <span className="flex-1" />
      <span className="min-w-0 text-right font-semibold break-all text-fg">{children}</span>
    </div>
  );
}

/** What the receipt calls the amount: "charged" only once the payment is confirmed. */
export function paymentLabel(order: Pick<Order, "payment_status">): string {
  switch (order.payment_status) {
    case "paid":
      return "Amount charged";
    case "pending":
      return "Amount (payment being confirmed)";
    default:
      return "Amount (not charged)";
  }
}

function Receipt({ order, item }: { order: Order; item?: OrderItem }) {
  return (
    <Section
      title="Receipt"
      action={
        order.invoice_id && (
          <Link
            to={`/billing/invoices/${order.invoice_id}`}
            className="text-[13px] font-semibold text-brand-500 hover:underline"
          >
            View invoice
          </Link>
        )
      }
    >
      <Rows>
        {item ? (
          <>
            <Row label="Domain">{item.fqdn}</Row>
            <Row label="Term">
              {item.kind === "transfer" ? "1 year added on transfer" : years(item.years)}
            </Row>
          </>
        ) : (
          <Row label="Domains">{order.items.length}</Row>
        )}
        <Row label={paymentLabel(order)}>{formatMoney(order.total_cents, order.currency)} incl. VAT</Row>
        <Row label="Payment method">{order.payment_method_label ?? "—"}</Row>
        <Row label="Confirmation">#{order.number}</Row>
      </Rows>
    </Section>
  );
}

// ── one domain ──────────────────────────────────────────────────────────────
function SingleItem({ order, item }: { order: Order; item: OrderItem }) {
  if (isFailed(item)) return <Failed order={order} item={item} />;
  switch (item.kind) {
    case "register":
      return inProgress(item) ? (
        <Registering order={order} item={item} />
      ) : (
        <Registered order={order} item={item} />
      );
    case "transfer":
      return inProgress(item) ? (
        <Transferring order={order} item={item} />
      ) : (
        <Transferred order={order} item={item} />
      );
    case "restore":
      return inProgress(item) ? (
        <Restoring order={order} item={item} />
      ) : (
        <Restored order={order} item={item} />
      );
    default:
      return inProgress(item) ? (
        <Renewing order={order} item={item} />
      ) : (
        <Renewed order={order} item={item} />
      );
  }
}

interface ItemProps {
  order: Order;
  item: OrderItem;
}

/** Figma 2080-4 "Registering your domain…", and 4724-5917 once it takes longer than usual. */
function Registering({ order, item }: ItemProps) {
  const now = useNow(10_000);
  const since = item.submitted_at ?? order.created_at;
  if (now - new Date(since).getTime() > SLOW_MS) return <SlowRegistration order={order} item={item} />;
  const paid = formatMoney(order.total_cents, order.currency);
  return (
    <DomainPage>
      <Crumbs order={order} label="registering domain" />
      <Heading
        size={24}
        tile={<HeadTile>{domainInitials(item.fqdn)}</HeadTile>}
        title={item.fqdn}
        status={
          <Badge size="md" tone="warning">
            Registration processing
          </Badge>
        }
        subtitle="Payment confirmed — waiting on the registry to finalize"
      />
      <Notice tone="processing" title="Registering your domain...">
        This usually takes under a minute. We'll notify you the moment it's confirmed — you can leave this
        page.
      </Notice>
      <ProgressSection
        title="Registration progress"
        steps={progressSteps(item, paid)}
        footnote="Availability isn't ownership — we'll only show this as active once the registry confirms it."
      />
    </DomainPage>
  );
}

function SlowRegistration({ order, item }: ItemProps) {
  const card = order.payment_method_label ? ` · ${order.payment_method_label}` : "";
  const sent = item.status === "submitted";
  return (
    <div className="flex min-h-[70vh] items-center justify-center py-8">
      <div className="flex w-full max-w-[640px] flex-col items-center gap-5 rounded-[20px] border border-border bg-surface p-6 text-center sm:p-8">
        <span className="flex size-14 items-center justify-center rounded-2xl bg-[#fef4e5]">
          <AlertTriangle className="size-6 text-[#e0a030]" />
        </span>
        <div className="flex flex-col gap-3">
          <h1 className="text-2xl leading-[30px] font-semibold break-all text-fg">
            We're confirming {item.fqdn} with the registry
          </h1>
          <p className="mx-auto max-w-[480px] text-sm text-[#5c5551]">
            Your payment went through, but the registry hasn't confirmed the registration yet. This usually
            resolves on its own. Please don't buy the domain again.
          </p>
        </div>
        <ul className="flex w-full flex-col gap-3.5 rounded-xl bg-[#faf6ef] p-4 text-left">
          <CheckItem
            state="done"
            title="Payment confirmed"
            detail={`${formatMoney(order.total_cents, order.currency)}${card} · ${clockTime(order.created_at)}`}
          />
          <CheckItem
            state={sent ? "done" : "active"}
            title="Registration sent to the registry"
            detail={sent ? clockTime(item.submitted_at) : "Sending now"}
          />
          <CheckItem
            state={sent ? "active" : "waiting"}
            title="Waiting for the registry to confirm"
            detail={
              sent
                ? `Checking automatically since ${clockTime(item.submitted_at)}`
                : "Starts once the registry has the request"
            }
          />
          <CheckItem
            state="waiting"
            title="Domain active"
            detail="We'll notify you as soon as it's confirmed"
          />
        </ul>
        <p className="w-full rounded-xl bg-[#eef8f3] p-3 text-left text-sm text-[#5c5551]">
          You won't be charged twice. If the registration can't be completed, it's refunded automatically and
          we'll tell you here.
        </p>
        <div className="flex flex-wrap justify-center gap-2.5">
          <GetHelpLink
            context={orderSupportContext(order, item, "Registration is taking longer than expected")}
            className="rounded-full"
          />
          <Link to="/domains" className={buttonClass("brand", "lg", "rounded-full")}>
            Go to domain main screen
          </Link>
        </div>
        <p className="text-xs text-fg-subtle">Support reference: {order.number}</p>
      </div>
    </div>
  );
}

function CheckItem({
  state,
  title,
  detail,
}: {
  state: "done" | "active" | "waiting";
  title: string;
  detail: string;
}) {
  return (
    <li className="flex items-start gap-3">
      {state === "done" ? (
        <CheckCircle2 className="mt-px size-[18px] shrink-0 text-[#007955]" />
      ) : state === "active" ? (
        <DomainIcon name="loading" size={18} className="mt-px animate-[spin_2.4s_linear_infinite]" />
      ) : (
        <Clock className="mt-px size-[18px] shrink-0 text-fg-subtle" />
      )}
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className={cx("text-sm font-semibold", state === "waiting" ? "text-fg-subtle" : "text-fg")}>
          {title}
        </span>
        <span className="text-xs text-fg-subtle">{detail}</span>
      </span>
    </li>
  );
}

/** Figma 2080-378 "<domain> is registered". */
function Registered({ order, item }: ItemProps) {
  const now = useNow(30_000);
  const detail = useDomainDetail(item.domain_id ?? undefined);
  const id = item.domain_id ?? "";
  return (
    <DomainPage>
      <Crumbs order={order} label="registered" />
      <Heading
        size={24}
        tile={<HeadTile>{domainInitials(item.fqdn)}</HeadTile>}
        title={`${item.fqdn} is registered`}
        status={
          <Badge size="md" tone="success">
            Active
          </Badge>
        }
        subtitle={`Confirmed by the registry ${when(item.completed_at, now)}`}
      />
      <Section title="What would you like to do next?">
        <ConnectNext domain={detail.data?.domain} domainId={id} />
        <NextStepEmail />
        <ConnectRow
          icon={<PanelTop className="size-5 text-[#d6342f]" />}
          tile="danger"
          title="Just register it for now"
          detail="Keep it unattached — you can connect it anytime"
          action={
            <Link to={`/domains/${id}`} className={buttonClass("secondary", "lg")}>
              Skip for now
            </Link>
          }
        />
      </Section>
      <TwoColumns
        main={<Receipt order={order} item={item} />}
        side={
          <Notice
            tone="cream"
            title="Find it in My Domains anytime"
            actions={
              <Link to="/domains" className={buttonClass("secondary", "lg")}>
                Go to My Domains
              </Link>
            }
          >
            {item.fqdn} now appears in Domains with full DNS, security and transfer controls.
          </Notice>
        }
      />
    </DomainPage>
  );
}

/** Figma 2080-772 "We couldn't complete this registration" (worded per kind). */
function Failed({ order, item }: ItemProps) {
  const refunded = item.status === "refunded";
  const cancelled = item.status === "cancelled";
  const charged = formatMoney(order.total_cents, order.currency);
  const refund = formatMoney(item.refunded_cents ?? order.total_cents, order.currency);
  const title = {
    register: "We couldn't complete this registration",
    transfer: "We couldn't complete this transfer",
    restore: "We couldn't restore this domain",
    renew: "We couldn't renew this domain",
  }[item.kind];
  const sld = item.fqdn.split(".")[0] ?? item.fqdn;
  const primary =
    item.kind === "register" ? (
      <Link to={`/domains/find?q=${encodeURIComponent(sld)}`} className={buttonClass("brand", "lg")}>
        Search for alternatives
      </Link>
    ) : item.kind === "transfer" ? (
      <Link to={resubmitTransferUrl(item.fqdn)} className={buttonClass("brand", "lg")}>
        Resubmit the transfer
      </Link>
    ) : (
      item.domain_id && (
        <Link to={`/domains/${item.domain_id}`} className={buttonClass("brand", "lg")}>
          Back to the domain
        </Link>
      )
    );
  return (
    <DomainPage>
      <Crumbs order={order} label={`failed ${item.kind === "register" ? "registration" : item.kind}`} />
      <Heading
        size={24}
        tile={
          <HeadTile tone="danger">
            <AlertTriangle className="size-5" />
          </HeadTile>
        }
        title={title}
        subtitle={
          cancelled
            ? "This unpaid order was cancelled before provider fulfilment"
            : "Your payment was not lost — see what happens next below"
        }
      />
      <Notice
        tone="danger"
        title={`${item.fqdn} could not be ${PAST[item.kind]}`}
        actions={
          <>
            {primary}
            <GetHelpLink context={orderSupportContext(order, item, title)} />
          </>
        }
      >
        {(item.failure_reason ?? "The registry rejected this request.").replace(/\.?$/, ".")}{" "}
        {cancelled
          ? "No payment was taken and the held domain name was released."
          : refunded
            ? `Your ${refund} refund has been issued — it can take 3-5 business days to reach your account.`
            : `Your ${charged} charge will be refunded automatically — no action needed from you.`}
      </Notice>
      {item.kind === "transfer" && <ProgressSection title="Transfer timeline" steps={transferSteps(item)} />}
      <Section title="What happens to your payment" className="w-full max-w-[480px]">
        <KeyValue variant="detail" label="Charge">
          <Badge size="md" tone="neutral">
            {charged}
            {order.payment_method_label ? ` · ${order.payment_method_label}` : ""}
          </Badge>
        </KeyValue>
        <KeyValue variant="detail" label="Status">
          <Badge size="md" tone={refunded ? "success" : "warning"}>
            {cancelled ? "No payment taken" : refunded ? "Refund issued" : "Refund initiated"}
          </Badge>
        </KeyValue>
        <KeyValue variant="detail" label="Expected by">
          <Badge size="md" tone="neutral">
            {cancelled ? "Order cancelled" : "In 3-5 business days"}
          </Badge>
        </KeyValue>
        {order.invoice_id && (
          <Link
            to={`/billing/invoices/${order.invoice_id}`}
            className="text-[13px] font-semibold text-brand-500 hover:underline"
          >
            View invoice #{order.number}
          </Link>
        )}
      </Section>
    </DomainPage>
  );
}

/** Figma 2077-4767 "Transfer in progress". There is no API to cancel a transfer, so no Cancel button. */
function Transferring({ order, item }: ItemProps) {
  return (
    <DomainPage>
      <Crumbs order={order} label="transferring" />
      <Heading
        size={24}
        tile={
          <HeadTile tone="pending">
            <DomainIcon name="globe" size={20} />
          </HeadTile>
        }
        title={item.fqdn}
        status={
          <>
            <Badge size="md" tone="warning">
              Transfer in progress
            </Badge>{" "}
            <HelpHint helpKey={HELP_KEYS.domains.transferStatus} label="transfer status" />
          </>
        }
        subtitle="Waiting on your current registrar to release the domain"
      />
      <Notice tone="warning" title="This domain still works normally">
        Your website and email on {item.fqdn} are unaffected during the transfer. Nothing to do right now
        unless your current registrar asks you to approve the release.
        {item.completes_by && ` It completes by ${longDate(item.completes_by)} at the latest.`}
      </Notice>
      <ProgressSection
        title="Transfer progress"
        steps={progressSteps(item, formatMoney(order.total_cents, order.currency))}
      />
    </DomainPage>
  );
}

/** Figma 2077-4938 "Now with CortexGrip". */
function Transferred({ order, item }: ItemProps) {
  const now = useNow(30_000);
  const detail = useDomainDetail(item.domain_id ?? undefined);
  const d = detail.data?.domain;
  const id = item.domain_id ?? "";
  return (
    <DomainPage>
      <Crumbs order={order} label="transferred" />
      <Heading
        size={24}
        tile={
          <HeadTile tone="pending">
            <DomainIcon name="globe" size={20} />
          </HeadTile>
        }
        title={item.fqdn}
        status={
          <Badge size="md" tone="success">
            Now with CortexGrip
          </Badge>
        }
        subtitle={`Transfer completed ${when(item.completed_at, now)}`}
      />
      <Section title="What would you like to do next?">
        <ConnectNext domain={d} domainId={id} />
        <NextStepEmail />
        <ConnectRow
          icon={<Settings className="size-5 text-[#d6342f]" />}
          tile="danger"
          title="Manage DNS and security"
          detail="Review records, nameservers and transfer lock"
          action={
            <Link to={`/domains/${id}`} className={buttonClass("secondary", "lg")}>
              Open Domain Control Centre
            </Link>
          }
        />
      </Section>
      <Section title="Domain details">
        <Rows>
          <Row label="Registrar">CortexGrip</Row>
          <Row label="Renews">
            {d ? `${longDate(d.expires_at)}${d.auto_renew ? " · auto-renew on" : " · auto-renew off"}` : "…"}
          </Row>
          <Row label="Transfer lock">{d ? (d.transfer_lock ? "On" : "Off") : "…"}</Row>
          <Row label="DNS">
            {d
              ? d.nameserver_mode === "custom"
                ? "Still at your previous DNS provider"
                : "Managed by CortexGrip"
              : "…"}
          </Row>
        </Rows>
      </Section>
    </DomainPage>
  );
}

/** Figma 2060-383 "Restoring your domain…". */
function Restoring({ order, item }: ItemProps) {
  return (
    <DomainPage>
      <Crumbs order={order} label="recovering" />
      <Heading
        size={24}
        tile={<HeadTile>{domainInitials(item.fqdn)}</HeadTile>}
        title={item.fqdn}
        status={
          <Badge size="md" tone="warning">
            Recovery processing
          </Badge>
        }
        subtitle="Your redemption payment was received — the registry is restoring this domain"
      />
      <Notice tone="processing" title="Restoring your domain...">
        This usually completes within a few hours, but can take up to 48 hours depending on the registry.
        We'll notify you the moment it's back.
      </Notice>
      <ProgressSection
        title="Recovery progress"
        steps={progressSteps(item, formatMoney(order.total_cents, order.currency))}
      />
    </DomainPage>
  );
}

/** Figma 2060-758 "<domain> is back" — only what is actually true of the domain now. */
function Restored({ order, item }: ItemProps) {
  const now = useNow(30_000);
  const detail = useDomainDetail(item.domain_id ?? undefined);
  const d = detail.data?.domain;
  const website: { label: string; tone: PillTone } | null = !d
    ? null
    : !d.site
      ? { label: "Not connected", tone: "neutral" }
      : d.connection === "connected"
        ? { label: "Live", tone: "success" }
        : d.connection === "error"
          ? { label: "Needs attention", tone: "danger" }
          : { label: "Reconnecting", tone: "warning" };
  return (
    <DomainPage>
      <Crumbs order={order} label="recovered" />
      <Heading
        size={24}
        tile={<HeadTile>{domainInitials(item.fqdn)}</HeadTile>}
        title={item.fqdn}
        status={
          <Badge size="md" tone="success">
            Recovered
          </Badge>
        }
        subtitle="Your domain is restored"
      />
      <Notice tone="success" title={`${item.fqdn} is back`}>
        Recovery completed {when(item.completed_at, now)}.
        {d?.site && d.connection === "connected" ? ` ${d.site.name} is reachable on it again.` : ""}
      </Notice>
      <Section title="What's back online">
        <KeyValue variant="detail" label="Website">
          {website ? (
            <Badge size="md" tone={website.tone}>
              {website.label}
            </Badge>
          ) : (
            <span className="text-sm text-fg-subtle">…</span>
          )}
        </KeyValue>
        <KeyValue variant="detail" label="Renewal date">
          <Badge size="md" tone="neutral">
            {d ? longDate(d.expires_at) : "…"}
          </Badge>
        </KeyValue>
        <KeyValue variant="detail" label="Auto-renew">
          {d ? (
            <Badge size="md" tone={d.auto_renew ? "success" : "neutral"}>
              {d.auto_renew ? "On" : "Off"}
            </Badge>
          ) : (
            <span className="text-sm text-fg-subtle">…</span>
          )}
        </KeyValue>
        {item.domain_id && (
          <Link to={`/domains/${item.domain_id}`} className={buttonClass("brand", "lg", "self-start")}>
            View domain overview
          </Link>
        )}
      </Section>
    </DomainPage>
  );
}

function Renewing({ order, item }: ItemProps) {
  return (
    <DomainPage>
      <Crumbs order={order} label="renewing" />
      <Heading
        size={24}
        tile={<HeadTile>{domainInitials(item.fqdn)}</HeadTile>}
        title={item.fqdn}
        status={
          <Badge size="md" tone="warning">
            Renewal processing
          </Badge>
        }
        subtitle={`Renewing for ${years(item.years)}`}
      />
      <Notice tone="processing" title="Renewing your domain...">
        Payment is confirmed and the registry is extending the registration. This usually takes under a minute
        — you can leave this page.
      </Notice>
      <ProgressSection
        title="Renewal progress"
        steps={progressSteps(item, formatMoney(order.total_cents, order.currency))}
      />
    </DomainPage>
  );
}

function Renewed({ order, item }: ItemProps) {
  const detail = useDomainDetail(item.domain_id ?? undefined);
  const d = detail.data?.domain;
  return (
    <DomainPage>
      <Crumbs order={order} label="renewed" />
      <Heading
        size={24}
        tile={<HeadTile tone="success">{domainInitials(item.fqdn)}</HeadTile>}
        title={`${item.fqdn} is renewed`}
        status={
          <Badge size="md" tone="success">
            Active
          </Badge>
        }
        subtitle={d ? `Registered until ${longDate(d.expires_at)}` : `Renewed for ${years(item.years)}`}
      />
      <Notice
        tone="success"
        title={`Renewed for ${years(item.years)}`}
        actions={
          item.domain_id && (
            <Link to={`/domains/${item.domain_id}`} className={buttonClass("brand", "lg")}>
              View domain overview
            </Link>
          )
        }
      >
        {d
          ? `${item.fqdn} is now registered until ${longDate(d.expires_at)}.`
          : "The registry confirmed the renewal."}
        {d ? ` Auto-renew is ${d.auto_renew ? "on" : "off"}.` : ""}
      </Notice>
      <Receipt order={order} item={item} />
    </DomainPage>
  );
}

// ── several domains ─────────────────────────────────────────────────────────
const ORDER_PILL: Record<Order["status"], { label: string; tone: PillTone }> = {
  pending_payment: { label: "Awaiting payment", tone: "warning" },
  payment_failed: { label: "Payment failed", tone: "danger" },
  paid: { label: "Processing", tone: "warning" },
  processing: { label: "Processing", tone: "warning" },
  completed: { label: "Completed", tone: "success" },
  partially_failed: { label: "Partly completed", tone: "warning" },
  failed: { label: "Failed", tone: "danger" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};

const KIND_LABEL: Record<OrderItem["kind"], string> = {
  register: "Registration",
  transfer: "Transfer",
  renew: "Renewal",
  restore: "Recovery",
};

function MultiItem({ order }: { order: Order }) {
  const pill = ORDER_PILL[order.status];
  const busy = order.items.some(inProgress);
  return (
    <DomainPage>
      <Breadcrumb
        items={[
          { label: "Domains", to: "/domains" },
          { label: "Orders", to: "/domains/orders" },
          { label: order.number },
        ]}
      />
      <Heading
        size={24}
        tile={
          <HeadTile>
            <ReceiptText className="size-5 text-brand-500" />
          </HeadTile>
        }
        title={`Order ${order.number}`}
        status={
          <Badge size="md" tone={pill.tone}>
            {pill.label}
          </Badge>
        }
        subtitle={`Placed ${longDate(order.created_at)} · ${order.items.length} domains · ${formatMoney(order.total_cents, order.currency)} incl. VAT`}
      />
      {busy && (
        <Notice tone="processing" title="Working on your domains...">
          Registrations usually take under a minute; transfers can take up to 5 days. This page updates by
          itself — you can leave it.
        </Notice>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {order.items.map((item) => (
          <ItemCard key={item.id} order={order} item={item} />
        ))}
      </div>
      <Receipt order={order} />
    </DomainPage>
  );
}

function ItemCard({ order, item }: { order: Order; item: OrderItem }) {
  const failed = isFailed(item);
  const done = item.status === "completed";
  const state: Step["state"] = failed ? "failed" : done ? "completed" : "in_progress";
  const term = item.kind === "transfer" ? "adds 1 year" : years(item.years);
  const link = "text-[13px] font-semibold text-brand-500 hover:underline";
  return (
    <Section
      title={<span className="break-all">{item.fqdn}</span>}
      subtitle={`${KIND_LABEL[item.kind]} · ${term} · ${formatMoney(item.net_cents, order.currency)} + VAT`}
      action={<StepPill state={state} />}
    >
      {inProgress(item) && (
        <div className="flex flex-col">
          {progressSteps(item, formatMoney(item.net_cents, order.currency)).map((s, i) => (
            <div key={i} className={cx("flex items-center gap-3 py-2", i > 0 && "border-t border-border")}>
              <p className="min-w-0 flex-1 text-[13px] text-[#5c5551]">{s.label}</p>
              <StepPill state={s.state} />
            </div>
          ))}
        </div>
      )}
      {done && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-[#5c5551]">
            {item.fqdn} is {PAST[item.kind]}
            {item.completed_at ? ` (${longDate(item.completed_at)})` : ""}.
          </p>
          {item.domain_id && (
            <div className="flex flex-wrap gap-4">
              {(item.kind === "register" || item.kind === "transfer") && (
                <LinkButton
                  to={`/domains/${item.domain_id}/connect`}
                  className={link}
                  requires="domains:manage"
                >
                  Connect to a website →
                </LinkButton>
              )}
              <Link to={`/domains/${item.domain_id}`} className={link}>
                Manage →
              </Link>
            </div>
          )}
        </div>
      )}
      {failed && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-[#5c5551]">
            {(item.failure_reason ?? "The registry rejected this request.").replace(/\.?$/, ".")}{" "}
            {item.status === "refunded"
              ? `${formatMoney(item.refunded_cents ?? item.net_cents, order.currency)} refunded — allow 3-5 business days.`
              : "This domain's share of the payment is being refunded automatically."}
          </p>
          {item.kind === "register" && (
            <Link
              to={`/domains/find?q=${encodeURIComponent(item.fqdn.split(".")[0] ?? item.fqdn)}`}
              className={link}
            >
              Search for alternatives →
            </Link>
          )}
          {item.kind === "transfer" && (
            <Link to={resubmitTransferUrl(item.fqdn)} className={link}>
              Resubmit the transfer →
            </Link>
          )}
        </div>
      )}
    </Section>
  );
}
